export type TokenOption = {
  grammarValue: string;
  displayValue: string;
  featureFlag?: string;
};

/**
 * Dropdown token maps
 * */
const numericAttributeTokenOptionsForTokenMap: Record<string, TokenOption[]> = {
  DRUG_EXPOSURE_INT_ATTRIBUTE: [
    { grammarValue: 'refills', displayValue: 'Refills' },
    { grammarValue: 'days_supply', displayValue: 'Days supply' },
  ],
  MEASUREMENT_FLOAT_ATTRIBUTE: [
    { grammarValue: 'range_high', displayValue: 'Range high' },
    { grammarValue: 'range_low', displayValue: 'Range low' },
  ],
  PERSON_INT_ATTRIBUTE: [
    { grammarValue: 'year_of_birth', displayValue: 'Year of birth' },
    { grammarValue: 'month_of_birth', displayValue: 'Month of birth' },
    { grammarValue: 'day_of_birth', displayValue: 'Day of birth' },
    { grammarValue: 'AGE_AT_INDEX', displayValue: 'Age at index' },
  ],
  "'value_as_number'": [{ grammarValue: 'value_as_number', displayValue: 'Value as number' }],
  "'quantity'": [{ grammarValue: 'quantity', displayValue: 'Quantity' }],
};

const dateTokenOptionsForTokenMap: Record<string, TokenOption[]> = {
  CONDITION_OCCURRENCE_DATE_ATTRIBUTE: [
    { grammarValue: 'condition_start_date', displayValue: 'Condition start date' },
    { grammarValue: 'condition_end_date', displayValue: 'Condition end date' },
  ],
  DRUG_EXPOSURE_DATE_ATTRIBUTE: [
    { grammarValue: 'drug_exposure_start_date', displayValue: 'Drug exposure start date' },
    { grammarValue: 'drug_exposure_end_date', displayValue: 'Drug exposure end date' },
  ],
  MEASUREMENT_DATE_ATTRIBUTE: [
    { grammarValue: 'measurement_date', displayValue: 'Measurement date' },
  ],
  "'measurement_date'": [{ grammarValue: 'measurement_date', displayValue: 'Measurement date' }],
  "'observation_date'": [{ grammarValue: 'observation_date', displayValue: 'Observation date' }],
  OBSERVATION_DATE_ATTRIBUTE: [
    { grammarValue: 'observation_date', displayValue: 'Observation date' },
  ],
  PROCEDURE_OCCURRENCE_DATE_ATTRIBUTE: [
    { grammarValue: 'procedure_date', displayValue: 'Procedure date' },
  ],
  "'procedure_date'": [{ grammarValue: 'procedure_date', displayValue: 'Procedure date' }],
  VISIT_OCCURRENCE_DATE_ATTRIBUTE: [
    { grammarValue: 'visit_start_date', displayValue: 'Visit start date' },
    { grammarValue: 'visit_end_date', displayValue: 'Visit end date' },
  ],
  PERSON_DATE_TIME_ATTRIBUTE: [{ grammarValue: 'birth_datetime', displayValue: 'Birth datetime' }],
  "'derived_event_date'": [
    { grammarValue: 'derived_event_date', displayValue: 'Derived event date' },
  ],
};

const conceptTokensForTokenMap: Record<string, TokenOption[]> = {
  CARE_SITE_CONCEPT_ATTRIBUTE: [
    { grammarValue: 'place_of_service_concept', displayValue: 'Place of service concept' },
  ],
  CONDITION_OCCURRENCE_CONCEPT_ATTRIBUTE: [
    { grammarValue: 'condition_concept', displayValue: 'Condition concept' },
    { grammarValue: 'condition_type_concept', displayValue: 'Condition type concept' },
    { grammarValue: 'condition_status_concept', displayValue: 'Condition status concept' },
    { grammarValue: 'condition_source_concept', displayValue: 'Condition source concept' },
  ],
  DRUG_EXPOSURE_CONCEPT_ATTRIBUTE: [
    { grammarValue: 'drug_concept', displayValue: 'Drug concept' },
    { grammarValue: 'drug_type_concept', displayValue: 'Drug type concept' },
    { grammarValue: 'route_concept', displayValue: 'Route concept' },
    { grammarValue: 'drug_source_concept', displayValue: 'Drug source concept' },
  ],
  MEASUREMENT_CONCEPT_ATTRIBUTE: [
    { grammarValue: 'measurement_concept', displayValue: 'Measurement concept' },
    { grammarValue: 'measurement_source_concept', displayValue: 'Measurement source concept' },
    { grammarValue: 'measurement_type_concept', displayValue: 'Measurement type concept' },
    { grammarValue: 'value_as_concept', displayValue: 'Value as concept' },
    { grammarValue: 'unit_concept', displayValue: 'Unit concept' },
    {
      grammarValue: 'unit_source_concept',
      displayValue: 'Unit source concept',
      featureFlag: 'RLORMeasurementUnitSourceUI',
    },
  ],
  OBSERVATION_CONCEPT_ATTRIBUTE: [
    { grammarValue: 'observation_concept', displayValue: 'Observation concept' },
    { grammarValue: 'observation_type_concept', displayValue: 'Observation type concept' },
    { grammarValue: 'value_as_concept', displayValue: 'Value as concept' },
    { grammarValue: 'unit_concept', displayValue: 'Unit concept' },
  ],
  PERSON_CONCEPT_ATTRIBUTE: [
    { grammarValue: 'gender_concept', displayValue: 'Gender concept' },
    { grammarValue: 'race_concept', displayValue: 'Race concept' },
    { grammarValue: 'ethnicity_concept', displayValue: 'Ethnicity concept' },
  ],
  PROCEDURE_OCCURRENCE_CONCEPT_ATTRIBUTE: [
    { grammarValue: 'procedure_concept', displayValue: 'Procedure concept' },
    { grammarValue: 'procedure_type_concept', displayValue: 'Procedure type concept' },
    { grammarValue: 'modifier_concept', displayValue: 'Modifier concept' },
  ],
  PROVIDER_CONCEPT_ATTRIBUTE: [
    { grammarValue: 'specialty_concept', displayValue: 'Specialty concept' },
  ],
  "'specialty_concept'": [{ grammarValue: 'specialty_concept', displayValue: 'Specialty concept' }],
  VISIT_OCCURRENCE_CONCEPT_ATTRIBUTE: [
    { grammarValue: 'visit_concept', displayValue: 'Visit concept' },
    { grammarValue: 'visit_source_concept', displayValue: 'Visit source concept' },
    { grammarValue: 'visit_type_concept', displayValue: 'Visit type concept' },
  ],
};

const comparators = {
  COMPARISON_OP: [
    { grammarValue: "'='", displayValue: '=' },
    { grammarValue: '!=', displayValue: '!=' },
    { grammarValue: '<', displayValue: '<' },
    { grammarValue: '>', displayValue: '>' },
    { grammarValue: '<=', displayValue: '<=' },
    { grammarValue: '>=', displayValue: '>=' },
  ],
  "'='": [{ grammarValue: '=', displayValue: '=' }],
  "'!='": [{ grammarValue: '!=', displayValue: '!=' }],
  "'<'": [{ grammarValue: '<', displayValue: '<' }],
  "'>'": [{ grammarValue: '>', displayValue: '>' }],
  "'<='": [{ grammarValue: '<=', displayValue: '<=' }],
  "'>='": [{ grammarValue: '>=', displayValue: '>=' }],
  "'IN'": [{ grammarValue: 'IN', displayValue: 'In' }],
  "'MAX'": [{ grammarValue: 'MAX', displayValue: 'Last' }],
  "'MIN'": [{ grammarValue: 'MIN', displayValue: 'First' }],
  "'INDEX'": [{ grammarValue: 'INDEX', displayValue: 'Index' }],
};

/**
 * Time comparator token maps
 */
const timeComparatorTokenOptionsForTokenMap: Record<string, TokenOption[]> = {
  "'ROLLING_WINDOW'": [{ grammarValue: 'ROLLING_WINDOW', displayValue: 'Rolling window' }],
  "'FIXED_WINDOW'": [{ grammarValue: 'FIXED_WINDOW', displayValue: 'Fixed window' }],
  "'MINIMUM_BETWEEN'": [
    { grammarValue: 'MINIMUM_BETWEEN', displayValue: 'Minimum time between occurrences' },
  ],
  "'MAXIMUM_BETWEEN'": [
    { grammarValue: 'MAXIMUM_BETWEEN', displayValue: 'Maximum time between occurrences' },
  ],
  "'TOTAL_BETWEEN'": [
    { grammarValue: 'TOTAL_BETWEEN', displayValue: 'Total time from first to last occurrence' },
  ],
  TIME_UNIT: [{ grammarValue: 'DAYS', displayValue: 'Days' }],
};

/**
 * Related entity dropdown token maps
 */
const entityTypeTokenOptionsForTokenMap: Record<string, TokenOption[]> = {
  "'CONDITION_OCCURRENCE'": [
    { grammarValue: 'CONDITION_OCCURRENCE', displayValue: 'Condition occurrence' },
  ],
  "'PROVIDER'": [{ grammarValue: 'PROVIDER', displayValue: 'Provider' }],
  "'VISIT_OCCURRENCE'": [{ grammarValue: 'VISIT_OCCURRENCE', displayValue: 'Visit occurrence' }],
};

const dropdownTokenOptionsForTokenMap: Record<string, TokenOption[]> = {
  ...comparators,
  ...conceptTokensForTokenMap,
  ...dateTokenOptionsForTokenMap,
  ...numericAttributeTokenOptionsForTokenMap,
  ...timeComparatorTokenOptionsForTokenMap,
  ...entityTypeTokenOptionsForTokenMap,
};

/**
 * integer input field token maps
 */
const intTokenOptionsForTokenMap: Record<string, TokenOption[]> = {
  INT: [{ grammarValue: 'INT', displayValue: '' }],
};

/**
 * integer input field token maps
 */
const floatTokenOptionsForTokenMap: Record<string, TokenOption[]> = {
  "'.'": [{ grammarValue: 'FLOAT', displayValue: '' }],
};

/**
 * Concept dropdown token maps
 */
const conceptDropdownTokenOptionsForTokenMap: Record<string, TokenOption[]> = {
  CONCEPT_GROUP: [{ grammarValue: 'CONCEPT_GROUP', displayValue: '' }],
  CONCEPT: [{ grammarValue: 'CONCEPT', displayValue: '' }],
};

const datePickerTokenOptionsForTokenMap: Record<string, TokenOption[]> = {
  DATE: [{ grammarValue: 'DATE', displayValue: '' }],
};

/**
 * Event name token maps
 */
const eventNameTokensForTokenMap: Record<string, TokenOption[]> = {
  CONDITION_OCCURRENCE_ALIAS: [{ grammarValue: 'CONDITION_OCCURRENCE_ALIAS', displayValue: '' }],
  DRUG_EXPOSURE_ALIAS: [{ grammarValue: 'DRUG_EXPOSURE_ALIAS', displayValue: '' }],
  MEASUREMENT_ALIAS: [{ grammarValue: 'MEASUREMENT_ALIAS', displayValue: '' }],
  OBSERVATION_ALIAS: [{ grammarValue: 'OBSERVATION_ALIAS', displayValue: '' }],
  PROCEDURE_OCCURRENCE_ALIAS: [{ grammarValue: 'PROCEDURE_OCCURRENCE_ALIAS', displayValue: '' }],
  VISIT_OCCURRENCE_ALIAS: [{ grammarValue: 'VISIT_OCCURRENCE_ALIAS', displayValue: '' }],
  MERGED_EVENT_NAME: [{ grammarValue: 'MERGED_EVENT_NAME', displayValue: '' }],
  SEQUENCE_EVENT_NAME: [{ grammarValue: 'SEQUENCE_EVENT_NAME', displayValue: '' }],
  INSTANCE_EVENT_NAME: [{ grammarValue: 'INSTANCE_EVENT_NAME', displayValue: '' }],
};

const qualifiedEventNameTokensForTokenMap: Record<string, TokenOption[]> = {
  QUALIFIED_CONDITION_OCCURRENCE_ALIAS: [
    { grammarValue: 'QUALIFIED_CONDITION_OCCURRENCE_ALIAS', displayValue: '' },
  ],
  QUALIFIED_DRUG_EXPOSURE_ALIAS: [
    { grammarValue: 'QUALIFIED_DRUG_EXPOSURE_ALIAS', displayValue: '' },
  ],
  QUALIFIED_MEASUREMENT_ALIAS: [{ grammarValue: 'QUALIFIED_MEASUREMENT_ALIAS', displayValue: '' }],
  QUALIFIED_OBSERVATION_ALIAS: [{ grammarValue: 'QUALIFIED_OBSERVATION_ALIAS', displayValue: '' }],
  QUALIFIED_PROCEDURE_OCCURRENCE_ALIAS: [
    { grammarValue: 'QUALIFIED_PROCEDURE_OCCURRENCE_ALIAS', displayValue: '' },
  ],
  QUALIFIED_VISIT_OCCURRENCE_ALIAS: [
    { grammarValue: 'QUALIFIED_VISIT_OCCURRENCE_ALIAS', displayValue: '' },
  ],
  QUALIFIED_MERGED_EVENT_NAME: [{ grammarValue: 'QUALIFIED_MERGED_EVENT_NAME', displayValue: '' }],
  QUALIFIED_SEQUENCE_EVENT_NAME: [
    { grammarValue: 'QUALIFIED_SEQUENCE_EVENT_NAME', displayValue: '' },
  ],
  QUALIFIED_INSTANCE_EVENT_NAME: [
    { grammarValue: 'QUALIFIED_INSTANCE_EVENT_NAME', displayValue: '' },
  ],
  QUALIFIED_DERIVED_EVENT_NAME: [
    { grammarValue: 'QUALIFIED_MERGED_EVENT_NAME', displayValue: '' },
    { grammarValue: 'QUALIFIED_SEQUENCE_EVENT_NAME', displayValue: '' },
    { grammarValue: 'QUALIFIED_INSTANCE_EVENT_NAME', displayValue: '' },
  ],
};

const subjectVariableEventNameTokensForTokenMap: Record<string, TokenOption[]> = {
  SUBJECT_VARIABLE_CONDITION_OCCURRENCE_ALIAS: [
    { grammarValue: 'CONDITION_OCCURRENCE_ALIAS', displayValue: '' },
  ],
  SUBJECT_VARIABLE_DRUG_EXPOSURE_ALIAS: [{ grammarValue: 'DRUG_EXPOSURE_ALIAS', displayValue: '' }],
  SUBJECT_VARIABLE_MEASUREMENT_ALIAS: [{ grammarValue: 'MEASUREMENT_ALIAS', displayValue: '' }],
  SUBJECT_VARIABLE_OBSERVATION_ALIAS: [{ grammarValue: 'OBSERVATION_ALIAS', displayValue: '' }],
  SUBJECT_VARIABLE_PROCEDURE_OCCURRENCE_ALIAS: [
    { grammarValue: 'PROCEDURE_OCCURRENCE_ALIAS', displayValue: '' },
  ],
  SUBJECT_VARIABLE_VISIT_OCCURRENCE_ALIAS: [
    { grammarValue: 'VISIT_OCCURRENCE_ALIAS', displayValue: '' },
  ],
  SUBJECT_VARIABLE_MERGED_EVENT_NAME: [{ grammarValue: 'MERGED_EVENT_NAME', displayValue: '' }],
  SUBJECT_VARIABLE_SEQUENCE_EVENT_NAME: [{ grammarValue: 'SEQUENCE_EVENT_NAME', displayValue: '' }],
  SUBJECT_VARIABLE_INSTANCE_EVENT_NAME: [{ grammarValue: 'INSTANCE_EVENT_NAME', displayValue: '' }],
  SUBJECT_VARIABLE_DERIVED_EVENT_NAME: [
    { grammarValue: 'MERGED_EVENT_NAME', displayValue: '' },
    { grammarValue: 'SEQUENCE_EVENT_NAME', displayValue: '' },
    { grammarValue: 'INSTANCE_EVENT_NAME', displayValue: '' },
  ],
};

const indexDateComboBoxTokensForTokenMap: Record<string, TokenOption[]> = {};

/**
 * Closing tokens to ignore
 */
const ignoredTokens: Record<string, TokenOption[]> = {
  "')'": [],
  "','": [],
  "'.'": [],
};

export const tokenOptionsForTokenMap: Record<string, TokenOption[]> = {
  ...dropdownTokenOptionsForTokenMap,
  ...intTokenOptionsForTokenMap,
  ...floatTokenOptionsForTokenMap,
  ...conceptDropdownTokenOptionsForTokenMap,
  ...eventNameTokensForTokenMap,
  ...qualifiedEventNameTokensForTokenMap,
  ...subjectVariableEventNameTokensForTokenMap,
  ...datePickerTokenOptionsForTokenMap,
  ...indexDateComboBoxTokensForTokenMap,
  ...ignoredTokens,
};

export const tokenTypeForTokenMap: Record<string, string> = {};
Object.keys(dropdownTokenOptionsForTokenMap).forEach((token) => {
  tokenTypeForTokenMap[token] = 'DROPDOWN';
});
Object.keys(intTokenOptionsForTokenMap).forEach((token) => {
  tokenTypeForTokenMap[token] = 'INT_NUMERIC_FIELD';
});
Object.keys(floatTokenOptionsForTokenMap).forEach((token) => {
  tokenTypeForTokenMap[token] = 'FLOAT_NUMERIC_FIELD';
});
Object.keys(conceptDropdownTokenOptionsForTokenMap).forEach((token) => {
  tokenTypeForTokenMap[token] = 'CONCEPT_COMBO_BOX';
});
Object.keys(eventNameTokensForTokenMap).forEach((token) => {
  tokenTypeForTokenMap[token] = 'COMBO_BOX';
});
Object.keys(qualifiedEventNameTokensForTokenMap).forEach((token) => {
  tokenTypeForTokenMap[token] = 'COMBO_BOX';
});
Object.keys(subjectVariableEventNameTokensForTokenMap).forEach((token) => {
  tokenTypeForTokenMap[token] = 'COMBO_BOX';
});
Object.keys(indexDateComboBoxTokensForTokenMap).forEach((token) => {
  tokenTypeForTokenMap[token] = 'COMBO_BOX';
});
Object.keys(datePickerTokenOptionsForTokenMap).forEach((token) => {
  tokenTypeForTokenMap[token] = 'DATE_PICKER';
});
